import { Link, useParams } from "react-router-dom";
import { Globe, Mail, Phone, MapPin, Instagram, Facebook, Linkedin, ArrowLeft } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { DemoBadge, Prose } from "@/components/common";
import { ActivityRow } from "@/components/ActivityCard";
import ShareBar from "@/components/ShareBar";
import NotFound from "@/pages/NotFound";
import { OrgMark } from "@/pages/Organizacoes";
import useTitle from "@/lib/useTitle";

export default function Organizacao() {
  const { slug } = useParams();
  const site = useSite();
  const o = site.data.organizations.find((x) => x.slug === slug);
  useTitle(o?.name || "Organização");
  if (!o) return <NotFound />;
  const acts = site.data.activities.filter((a) => (a.organization_ids || []).includes(o.id));
  const space = site.spaces[o.space_id];
  const links = [[o.website, Globe, "Website"], [o.email && `mailto:${o.email}`, Mail, o.email], [o.phone && `tel:${o.phone}`, Phone, o.phone], [o.instagram, Instagram, "Instagram"], [o.facebook, Facebook, "Facebook"], [o.linkedin, Linkedin, "LinkedIn"]].filter(([h]) => h);
  return (
    <article className="container-x py-10" data-testid="org-page">
      <Link to="/organizacoes" className="inline-flex items-center gap-2 text-sm font-semibold min-h-[44px]" data-testid="org-back-link"><ArrowLeft className="h-4 w-4" aria-hidden />Organizações</Link>
      <div className="mt-6 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <OrgMark org={o} size="h-24 w-full max-w-[280px] object-left text-3xl" />
          <p className="eyebrow text-[var(--clay)] mt-6">{o.area}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[0.95] mt-2" data-testid="org-name">{o.name}</h1>
          <DemoBadge show={o.demo} className="mt-3" />
          <Prose text={o.description} className="mt-6 text-base md:text-lg" />
          <div className="mt-8"><ShareBar title={o.name} path={`/organizacoes/${o.slug}`} /></div>
        </div>
        <aside className="lg:col-span-5 bg-white rounded-3xl border border-black/5 p-6 self-start">
          <p className="eyebrow text-slate-500">Contactos públicos</p>
          {links.length ? <ul className="mt-3 space-y-2">{links.map(([h, Icon, l]) => <li key={l}><a href={h} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 underline min-h-[44px]"><Icon className="h-4 w-4" aria-hidden />{l}</a></li>)}</ul> : <p className="mt-2 text-sm text-slate-600">Contactos a publicar pela organização.</p>}
          {space && <><p className="eyebrow text-slate-500 mt-6">No recinto</p><Link to={`/mapa?espaco=${space.slug}`} className="mt-2 inline-flex items-center gap-2 font-semibold underline" data-testid="org-space-link"><MapPin className="h-4 w-4" aria-hidden />{space.code ? `${space.code} · ` : ""}{space.name}</Link></>}
        </aside>
      </div>
      {acts.length > 0 && (
        <section className="mt-14" aria-labelledby="org-acts">
          <h2 id="org-acts" className="font-display text-3xl font-extrabold mb-5">Atividades dinamizadas</h2>
          <div className="grid gap-3">{acts.map((a) => <ActivityRow key={a.id} act={a} />)}</div>
        </section>
      )}
    </article>
  );
}
